import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    OrganisationService,
    OrganisationSettingsDto,
    PersonsOrganisationDto,
    UpdateOrganisationSettingsCommand
} from '@flyfreely-portal-ui/flyfreely';
import { firstValueFrom, Subject, takeUntil, tap } from 'rxjs';

export const DEFAULT_DASHBOARD = 'DEFAULT';

export interface UpdateOrganisationSettingsForm {
    instantMissionWorkflowId: FormControl<number>;
    defaultMissionWorkflowId: FormControl<number>;
    defaultOperationTypeId: FormControl<number>;
    defaultMaximumHeight: FormControl<number>;
    defaultDashboard: FormControl<string>;
}

@Injectable()
export class OrganisationSettingsService {
    private organisation: PersonsOrganisationDto;

    settingsForm: FormGroup<UpdateOrganisationSettingsForm> = new FormGroup({
        defaultMaximumHeight: new FormControl<number>(undefined),
        defaultMissionWorkflowId: new FormControl<number>(undefined),
        defaultOperationTypeId: new FormControl<number>(undefined),
        instantMissionWorkflowId: new FormControl<number>(undefined),
        defaultDashboard: new FormControl<string>(DEFAULT_DASHBOARD)
    });

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private organisationService: OrganisationService,
        private logging: FlyFreelyLoggingService
    ) {}

    updateOrganisation(organisation: PersonsOrganisationDto) {
        if (organisation.id !== this.organisation?.id) {
            this.organisation = organisation;
            this.updateSettingsFormValues(organisation.settings);
        }
    }

    private updateSettingsFormValues(
        organisationSettings: OrganisationSettingsDto
    ) {
        this.settingsForm.patchValue(
            {
                ...organisationSettings
            },
            { emitEvent: false }
        );
        this.settingsForm.markAsPristine();
    }

    updateSettings(): Promise<void> {
        if (this.organisation == null) {
            return Promise.resolve();
        }
        const value = this.settingsForm.value;
        const command: UpdateOrganisationSettingsCommand = {
            defaultMaximumHeight: value.defaultMaximumHeight,
            defaultMissionWorkflowId: value.defaultMissionWorkflowId,
            defaultOperationTypeId: value.defaultOperationTypeId,
            instantMissionWorkflowId: value.instantMissionWorkflowId,
            defaultDashboard:
                value.defaultDashboard === DEFAULT_DASHBOARD
                    ? undefined
                    : value.defaultDashboard
        };
        return firstValueFrom(
            this.organisationService
                .updateOrganisationSettings(this.organisation.id, command)
                .pipe(
                    takeUntil(this.ngUnsubscribe$),
                    tap({
                        next: () => {
                            // update settings form
                            this.logging.success(
                                'Your default settings have been updated'
                            );
                            this.updateSettingsFormValues(
                                command as OrganisationSettingsDto
                            );
                        },
                        error: (error: FlyFreelyError) => {
                            this.logging.error(
                                error,
                                `Error updating default organisation settings: ${error.message}`
                            );
                        }
                    })
                )
        ).then(() => undefined);
    }
}
